import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, get, put } from 'utils/api';
import { logError } from 'utils/log';
import {
  getConfigOptions
} from 'modules/insights/sov/utils/consumer-insights/sov-consumer-insights';

import type { Action, Dispatch, ThunkAction } from 'types';
import type {
  SOVGeneralAuthInfo,
  SOVRequestAccessResponse
} from 'modules/insights/sov/types/consumer-insights';

const { CI } = API.END_POINTS;

const ACCESS_PROCESSING = 'sov-ci/ACCESS_PROCESSING';
const ACCESS_LOADED = 'sov-ci/ACCESS_LOADED';
const ACCESS_UPDATED = 'sov-ci/ACCESS_UPDATED';
const ACCESS_RESET = 'sov-ci/ACCESS_RESET';

const initialState = {
  accessRequestResponse: {},
  isLoading: false
};

const setStatus = (
  status: boolean
): Action => ({
  type: ACCESS_PROCESSING,
  payload: {
    status
  }
});

const accessRequestLoaded = (
  response: SOVRequestAccessResponse
): Action => ({
  type: ACCESS_LOADED,
  payload: {
    response
  }
});

const accessRequestUpdated = (
  response: SOVRequestAccessResponse
): Action => ({
  type: ACCESS_UPDATED,
  payload: {
    response
  }
});

export const resetAccess = (): Action => ({
  type: ACCESS_RESET
});

export const checkAccess = (
  authInfo: Partial<SOVGeneralAuthInfo>
): ThunkAction => (
  async (dispatch: Dispatch) => {
    dispatch(setStatus(true));

    try {
      const url = CI.SOV.checkAccess();
      const configOptions = getConfigOptions(authInfo);
      const response = await get(url, configOptions.config, configOptions.options);
      const responseData = response.data || {};

      dispatch(setStatus(false));

      return dispatch(accessRequestLoaded(responseData));
    } catch (error) {
      logError(error);
      throw error;
    } finally {
      dispatch(setStatus(false));
    }
  }
);

export const updateAccess = (
  authInfo: Partial<SOVGeneralAuthInfo>,
  data: SOVRequestAccessResponse
): ThunkAction => (
  async (dispatch: Dispatch) => {
    dispatch(setStatus(true));

    try {
      const url = CI.SOV.updateAccess();
      const configOptions = getConfigOptions(authInfo);

      const updatedConfigOptions = {
        options: configOptions.options,
        config: {
          ...configOptions.config,
          data
        }
      };

      const response = await put(url, updatedConfigOptions.config, updatedConfigOptions.options);
      const responseData = response.data || {};

      dispatch(setStatus(false));

      return dispatch(accessRequestUpdated(responseData));
    } catch (error) {
      logError(error);
      throw error;
    } finally {
      dispatch(setStatus(false));
    }
  }
);

export default createReducer<any, any>('sovAccessConsumerInsights', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [ACCESS_RESET]: () => initialState,
  [ACCESS_PROCESSING]: (state, { status }) => ({
    ...state,
    isLoading: status
  }),
  [ACCESS_LOADED]: (state, { response }) => ({
    ...state,
    accessRequestResponse: response,
    isLoading: false
  }),
  [ACCESS_UPDATED]: (state, { response }) => ({
    ...state,
    accessRequestResponse: response,
    isLoading: false
  })
});
